import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const ShipmentDetails = ({ shipment }) => {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const detailSections = [
    {
      id: 'package',
      title: 'Package Details',
      icon: 'Package',
      content: (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm font-body text-muted-foreground">Weight:</span>
            <span className="text-sm font-body font-semibold text-foreground">{shipment?.packageDetails?.weight}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-body text-muted-foreground">Dimensions:</span>
            <span className="text-sm font-body font-semibold text-foreground">{shipment?.packageDetails?.dimensions}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-body text-muted-foreground">Contents:</span>
            <span className="text-sm font-body font-semibold text-foreground">{shipment?.packageDetails?.contents}</span>
          </div>
        </div>
      )
    },
    {
      id: 'delivery',
      title: 'Delivery Information',
      icon: 'Truck',
      content: (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm font-body text-muted-foreground">Carrier:</span>
            <span className="text-sm font-body font-semibold text-foreground">{shipment?.carrier}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-body text-muted-foreground">Estimated Delivery:</span>
            <span className="text-sm font-body font-semibold text-primary">{shipment?.estimatedDelivery}</span>
          </div>
          <div className="flex justify-between items-start">
            <span className="text-sm font-body text-muted-foreground">Current Location:</span>
            <span className="text-sm font-body font-semibold text-foreground text-right">{shipment?.currentLocation}</span>
          </div>
        </div>
      )
    },
    {
      id: 'addresses',
      title: 'Shipping Addresses',
      icon: 'MapPin',
      content: (
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Icon name="ArrowUp" size={16} className="text-primary" />
              <span className="text-sm font-body font-semibold text-foreground">Origin</span>
            </div>
            <p className="text-sm font-body text-muted-foreground ml-6">{shipment?.origin?.address}</p>
            <p className="text-sm font-body text-muted-foreground ml-6 mt-1">Contact: {shipment?.origin?.contact}</p>
          </div>
          <div className="border-t border-border pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="ArrowDown" size={16} className="text-primary" />
              <span className="text-sm font-body font-semibold text-foreground">Destination</span>
            </div>
            <p className="text-sm font-body text-muted-foreground ml-6">{shipment?.destination?.address}</p>
            <p className="text-sm font-body text-muted-foreground ml-6 mt-1">Contact: {shipment?.destination?.contact}</p>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg shadow-warm overflow-hidden">
      <div className="p-6 border-b border-border">
        <h2 className="text-xl font-heading font-bold text-foreground">
          Shipment Details
        </h2>
      </div>
      <div className="divide-y divide-border">
        {detailSections?.map((section, index) => (
          <motion.div
            key={section?.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            <button
              onClick={() => toggleSection(section?.id)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-muted/50 transition-colors duration-200"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name={section?.icon} size={20} className="text-primary" />
                </div>
                <span className="text-base font-body font-semibold text-foreground">
                  {section?.title}
                </span>
              </div>
              <Icon
                name="ChevronDown"
                size={20}
                className={`text-muted-foreground transition-transform duration-200 ${
                  expandedSection === section?.id ? 'rotate-180' : ''
                }`}
              />
            </button>

            {expandedSection === section?.id && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="px-6 pb-4"
              >
                <div className="pl-13">
                  {section?.content}
                </div>
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>
      {/* Action Buttons */}
      <div className="p-6 bg-muted/30 border-t border-border">
        <div className="flex flex-wrap gap-3">
          <button className="flex-1 min-w-[140px] px-4 py-2.5 bg-background border border-border rounded-lg text-sm font-body font-medium text-foreground hover:bg-muted transition-all duration-200 flex items-center justify-center gap-2">
            <Icon name="Bell" size={16} />
            <span>Get Updates</span>
          </button>
          <button className="flex-1 min-w-[140px] px-4 py-2.5 bg-background border border-border rounded-lg text-sm font-body font-medium text-foreground hover:bg-muted transition-all duration-200 flex items-center justify-center gap-2">
            <Icon name="Share2" size={16} />
            <span>Share Tracking</span>
          </button>
          <button className="flex-1 min-w-[140px] px-4 py-2.5 bg-background border border-border rounded-lg text-sm font-body font-medium text-foreground hover:bg-muted transition-all duration-200 flex items-center justify-center gap-2">
            <Icon name="Download" size={16} />
            <span>Download PDF</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShipmentDetails;